import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Resource } from "../../core/models/wrappers/Resource";
import { useApiClient } from "../clients/ApiClient";
import {
  ApprovalMailCurrentStatus,
  ApprovalMailLogBatchDto,
  ApprovalMailLogItemDto,
  ApprovalMailLogMetaDto,
  ApprovalMailLogQuery,
  ApprovalMailLogResultDto,
  ApprovalMailLogSummaryDto,
  ApprovalMailMode,
  ApprovalMailSettingsDto,
  ApprovalMailFlagsDto,
  ApprovalMailDefaultsDto,
  ApprovalMailStatsCellDto,
  ApprovalMailStatsDayDto,
  ApprovalMailStatsDto,
  ApprovalMailStatsQuery,
  ApprovalMailStatsRecipientDto,
  ApprovalMailStatsStatusDto,
  ApprovalMailStatsTotalDto,
  ApprovalMailPreferencesDto,
  ApprovalMailPreferencesEffectiveDto,
  ApprovalMailPreferencesFlagsDto,
  ApprovalReminderGroupDto,
  ApprovalReminderRunDto,
  ApprovalReminderRunQuery,
} from "../models/responses/ApprovalMailDto";
import { PutApprovalMailSettingsRequest } from "../models/bodies/PutApprovalMailSettingsRequest";
import { PutApprovalMailPreferencesRequest } from "../models/bodies/PutApprovalMailPreferencesRequest";

// Approval e-mails per organisation (flag REACT_APP_APPROVAL_MAIL_SETTINGS_
// ENABLED). Fixed call budget: the settings are ONE GET the first time the
// section is opened for an org (5 min stale, no polling), the log is ONE
// GET per (org, filter) with the same staleness, Save is ONE PUT whose
// answer (the full settings, same shape as the GET) is written straight
// into the settings cache - no refetch (the log is not touched - a setting
// changes nothing in it). Both queries run with retry: false - the app's
// QueryClient sets no retry, so react-query's default (3 retries with
// back-off) would turn a 404 (API flag off) into FOUR calls and a ~7 s wait
// before the section can say "not active yet". Queries stay disabled until
// the caller says the section is open (enabled) - 0 calls elsewhere.
//
// Round 2 (status + statistics): the log answer also carries each item's
// CURRENT status, the mail's own outcome and a resume. They travel in the
// SAME GET: the query caches { batches, meta }, useLog hands out the batches
// exactly as before and useLogMeta reads the meta from the same cache entry
// (same key = ONE request for both observers). The admin page "Approval
// mails" (flag REACT_APP_APPROVAL_MAIL_STATS_ENABLED) reads useStats: ONE
// GET ops/approval-mail/stats per (from, to, orgId), 60 s stale, no retry,
// no polling; 0 calls while disabled.
export interface ApprovalMailRepository {
  useSettings(
    orgId: number,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailSettingsDto>;
  useLog(
    orgId: number,
    query?: ApprovalMailLogQuery,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailLogBatchDto[]>;
  // The fields the log answer carries NEXT TO its batches (resume, status
  // lookup failed). Same key as useLog: never a request of its own.
  useLogMeta(
    orgId: number,
    query?: ApprovalMailLogQuery,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailLogMetaDto>;
  // Admin statistics: when and to whom approval e-mails were sent.
  useStats(
    query: ApprovalMailStatsQuery,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailStatsDto>;
  // PUT the changed fields; the API's answer (the full settings) replaces
  // the org's cached settings so the fields show the server state (1 PUT,
  // 0 GET). An empty answer (an older API) falls back to ONE refetch.
  // Rejects on failure (404 = API flag off).
  setSettings(orgId: number, body: PutApprovalMailSettingsRequest): Promise<void>;
  // The caller's OWN preference (My profile "My approval e-mails", flag
  // REACT_APP_APPROVAL_MAIL_SELFSERVICE_ENABLED): ONE GET the first time
  // the section is opened (5 min stale, no retry, no polling); 0 calls
  // while disabled. Key ['approvalMail', 'preferences'] - no org/user
  // segment, the row is the token's.
  usePreferences(options?: {
    enabled?: boolean;
  }): Resource<ApprovalMailPreferencesDto>;
  // PUT the changed fields (null = follow the organisation); the API's
  // answer (the full preferences) replaces the cached row (1 PUT, 0 GET).
  // An empty answer (an older API) falls back to ONE refetch. Rejects on
  // failure (404 = API flag off).
  setPreferences(body: PutApprovalMailPreferencesRequest): Promise<void>;
  // "Run reminders" (admin page "Approval mails", flag
  // REACT_APP_MAIL_TEST_APPROVAL_RUN_ENABLED): ONE POST ops/approval-mail/
  // reminders/run per click, the normalised sweep result back. A REAL run
  // (dryRun false) also invalidates the stats queries - ONE GET for the
  // mounted period so the tables follow; a dry run touches nothing.
  // Rejects on failure (404 = an API flag off).
  runReminders(query: ApprovalReminderRunQuery): Promise<ApprovalReminderRunDto>;
}

export const APPROVAL_MAIL_ROOT_KEY = "approvalMail";
export const APPROVAL_MAIL_LOG_DEFAULT_TAKE = 200;
export const APPROVAL_MAIL_REMINDER_COUNT_MAX = 3;
export const APPROVAL_MAIL_INTERVAL_MIN_DAYS = 1;
export const APPROVAL_MAIL_INTERVAL_MAX_DAYS = 14;
const STALE_MS = 5 * 60 * 1000;
const STATS_STALE_MS = 60 * 1000;

const asRecord = (raw: unknown): Record<string, unknown> =>
  raw !== null && typeof raw === "object" && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : {};

const text = (value: unknown): string | null =>
  value === null || value === undefined ? null : String(value);

const int = (value: unknown): number | null =>
  typeof value === "number" && Number.isFinite(value)
    ? Math.trunc(value)
    : typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))
      ? Math.trunc(Number(value))
      : null;

const bool = (value: unknown, fallback: boolean): boolean =>
  typeof value === "boolean" ? value : fallback;

const clamp = (value: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, value));

export function normalizeApprovalMailMode(value: unknown): ApprovalMailMode {
  return typeof value === "string" && value.toLowerCase() === "peritem"
    ? "perItem"
    : "digest";
}

function normalizeFlags(raw: unknown): ApprovalMailFlagsDto | undefined {
  const r = asRecord(raw);
  const out: ApprovalMailFlagsDto = {
    ...(typeof r.perOrgSettingsEnabled === "boolean"
      ? { perOrgSettingsEnabled: r.perOrgSettingsEnabled }
      : {}),
    ...(typeof r.reminderEnabled === "boolean"
      ? { reminderEnabled: r.reminderEnabled }
      : {}),
    ...(typeof r.logEnabled === "boolean" ? { logEnabled: r.logEnabled } : {}),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

function normalizeDefaults(raw: unknown): ApprovalMailDefaultsDto | undefined {
  const r = asRecord(raw);
  const count = int(r.reminderCount);
  const interval = int(r.reminderIntervalDays);
  const out: ApprovalMailDefaultsDto = {
    ...(count === null ? {} : { reminderCount: clamp(count, 0, APPROVAL_MAIL_REMINDER_COUNT_MAX) }),
    ...(interval === null
      ? {}
      : {
          reminderIntervalDays: clamp(
            interval,
            APPROVAL_MAIL_INTERVAL_MIN_DAYS,
            APPROVAL_MAIL_INTERVAL_MAX_DAYS,
          ),
        }),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

// The raw JSON of GET .../approval-mail/settings made safe for the section:
// every field present, counts clamped to the API's own ranges (0..3 / 1..14);
// the top-level values are the EFFECTIVE ones (the API applies its defaults
// before answering); the profile gate (wire: newDesign/newLogos, also
// designs/logos), the API's flags and defaults only when sent. Accepts the
// optional effective values flat (effectiveReminderCount) or nested
// (effective.reminderCount) so the FE can go first against either wire.
export function normalizeApprovalMailSettings(
  raw: unknown,
): ApprovalMailSettingsDto {
  const body = asRecord(raw);
  const effective = asRecord(body.effective);
  const gate = asRecord(body.profileGate);
  const gateDesigns = gate.newDesign ?? gate.designs;
  const gateLogos = gate.newLogos ?? gate.logos;
  const flags = normalizeFlags(body.flags);
  const defaults = normalizeDefaults(body.defaults);
  const modeSupported = bool(body.modeSupported, false);
  const count = int(body.reminderCount);
  const interval = int(body.reminderIntervalDays);
  const effCount = int(body.effectiveReminderCount ?? effective.reminderCount);
  const effInterval = int(
    body.effectiveReminderIntervalDays ?? effective.reminderIntervalDays,
  );
  const effEnabled = body.effectiveEnabled ?? effective.enabled;
  const effMode = body.effectiveMode ?? effective.mode;
  return {
    enabled: bool(body.enabled, true),
    mode: modeSupported ? normalizeApprovalMailMode(body.mode) : "digest",
    modeSupported,
    reminderCount:
      count === null ? 1 : clamp(count, 0, APPROVAL_MAIL_REMINDER_COUNT_MAX),
    reminderIntervalDays:
      interval === null
        ? 3
        : clamp(interval, APPROVAL_MAIL_INTERVAL_MIN_DAYS, APPROVAL_MAIL_INTERVAL_MAX_DAYS),
    ...(typeof effEnabled === "boolean" ? { effectiveEnabled: effEnabled } : {}),
    ...(typeof effMode === "string"
      ? { effectiveMode: normalizeApprovalMailMode(effMode) }
      : {}),
    ...(effCount === null
      ? {}
      : { effectiveReminderCount: clamp(effCount, 0, APPROVAL_MAIL_REMINDER_COUNT_MAX) }),
    ...(effInterval === null
      ? {}
      : {
          effectiveReminderIntervalDays: clamp(
            effInterval,
            APPROVAL_MAIL_INTERVAL_MIN_DAYS,
            APPROVAL_MAIL_INTERVAL_MAX_DAYS,
          ),
        }),
    ...(typeof gateDesigns === "boolean" || typeof gateLogos === "boolean"
      ? {
          profileGate: {
            designs: bool(gateDesigns, true),
            logos: bool(gateLogos, true),
          },
        }
      : {}),
    ...(flags ? { flags } : {}),
    ...(defaults ? { defaults } : {}),
  };
}

const CURRENT_STATUS: Record<string, ApprovalMailCurrentStatus> = {
  pending: "pending",
  approved: "approved",
  rejected: "rejected",
  deleted: "deleted",
  notfound: "notFound",
  unknown: "unknown",
};

// The item's status NOW as the API looked it up: the known values (any
// casing), "unknown" for a value this build does not know, undefined when
// the API sent none (an older API, a failed lookup) = no status pill.
export function normalizeApprovalMailCurrentStatus(
  value: unknown,
): ApprovalMailCurrentStatus | undefined {
  if (typeof value !== "string" || value.trim() === "") return undefined;
  return CURRENT_STATUS[value.trim().toLowerCase()] ?? "unknown";
}

function normalizeItem(raw: unknown): ApprovalMailLogItemDto | null {
  const r = asRecord(raw);
  const itemId = int(r.itemId);
  if (itemId === null) return null;
  const currentStatus = normalizeApprovalMailCurrentStatus(r.currentStatus);
  return {
    itemType: (text(r.itemType) ?? "").toLowerCase(),
    itemId,
    reminderNo: int(r.reminderNo),
    outcome: text(r.outcome),
    // Only when the API sends it per item (absent = the batch's value).
    ...("linkOrganizationId" in r
      ? { linkOrganizationId: int(r.linkOrganizationId) }
      : {}),
    // Only when the API looked it up (absent = no pill).
    ...(currentStatus ? { currentStatus } : {}),
  };
}

// The raw JSON of GET .../approval-mail/log made safe for the section: an
// array of batches (a bare array, { batches: [] } or { items: [] } - all
// accepted), each with every field present and its items an array (a row
// without a usable batchId/sentAtUtc is dropped). Newest first, as the API
// sends it - the section never re-sorts.
export function normalizeApprovalMailLog(raw: unknown): ApprovalMailLogBatchDto[] {
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(asRecord(raw).batches)
      ? (asRecord(raw).batches as unknown[])
      : Array.isArray(asRecord(raw).items)
        ? (asRecord(raw).items as unknown[])
        : [];
  return list
    .map((entry): ApprovalMailLogBatchDto | null => {
      const r = asRecord(entry);
      const batchId = text(r.batchId ?? r.mailBatchId);
      const sentAtUtc = text(r.sentAtUtc);
      if (!batchId || !sentAtUtc) return null;
      const items = (Array.isArray(r.items) ? r.items : [])
        .map(normalizeItem)
        .filter((i): i is ApprovalMailLogItemDto => i !== null);
      return {
        batchId,
        sentAtUtc,
        kind: (text(r.kind) ?? "").toLowerCase(),
        mailReminderNo: int(r.mailReminderNo),
        reminderMax: int(r.reminderMax),
        source: (text(r.source) ?? "").toLowerCase(),
        direction: (text(r.direction) ?? "").toLowerCase(),
        recipient: text(r.recipient),
        template: text(r.template),
        linkOrganizationId: int(r.linkOrganizationId),
        counterpartOrganizationId: int(r.counterpartOrganizationId),
        language: text(r.language),
        items,
        // Only when the API sends the mail's own outcome.
        ...("outcome" in r ? { outcome: text(r.outcome) } : {}),
      };
    })
    .filter((b): b is ApprovalMailLogBatchDto => b !== null);
}

const tally = (value: unknown): number => Math.max(0, int(value) ?? 0);

function normalizeLogSummary(raw: unknown): ApprovalMailLogSummaryDto | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const r = asRecord(raw);
  return {
    sentMails: tally(r.sentMails),
    reminderMails: tally(r.reminderMails),
    skippedMails: tally(r.skippedMails),
    itemsPending: tally(r.itemsPending),
    itemsApproved: tally(r.itemsApproved),
    itemsRejected: tally(r.itemsRejected),
    itemsGone: tally(r.itemsGone),
  };
}

// What the log answer carries NEXT TO its batches: the resume only when the
// API sent one (a bare array / an older API = none: the section computes
// it from the batches), statusError true only when the API said so.
export function normalizeApprovalMailLogMeta(raw: unknown): ApprovalMailLogMetaDto {
  const body = asRecord(raw);
  const summary = normalizeLogSummary(body.summary);
  return {
    ...(summary ? { summary } : {}),
    statusError: body.statusError === true,
  };
}

// The whole log answer as the query caches it (ONE GET): the batches
// exactly as normalizeApprovalMailLog gives them + the meta.
export function normalizeApprovalMailLogResult(raw: unknown): ApprovalMailLogResultDto {
  return {
    batches: normalizeApprovalMailLog(raw),
    meta: normalizeApprovalMailLogMeta(raw),
  };
}

// ----- Statistics -----

// first | reminder1..3 | closed | skippedAllowlist - lowercased, with the
// one camel-cased kind restored so the page's columns can match on it.
export function normalizeApprovalMailStatsKind(value: unknown): string {
  const kind = (text(value) ?? "").trim().toLowerCase();
  return kind === "skippedallowlist" ? "skippedAllowlist" : kind;
}

function normalizeStatsTotal(raw: unknown): ApprovalMailStatsTotalDto {
  const r = asRecord(raw);
  return {
    kind: normalizeApprovalMailStatsKind(r.kind),
    mails: tally(r.mails),
    items: tally(r.items),
  };
}

function normalizeStatsCell(raw: unknown): ApprovalMailStatsCellDto {
  const r = asRecord(raw);
  return {
    ...normalizeStatsTotal(raw),
    direction: (text(r.direction) ?? "").toLowerCase(),
    source: (text(r.source) ?? "").toLowerCase(),
  };
}

const objects = (value: unknown): unknown[] =>
  (Array.isArray(value) ? value : []).filter(
    (entry) => entry !== null && typeof entry === "object" && !Array.isArray(entry),
  );

function normalizeStatsStatus(raw: unknown): ApprovalMailStatsStatusDto | undefined {
  if (raw === null || typeof raw !== "object" || Array.isArray(raw)) {
    return undefined;
  }
  const r = asRecord(raw);
  return {
    items: tally(r.items),
    pending: tally(r.pending),
    approved: tally(r.approved),
    rejected: tally(r.rejected),
    deleted: tally(r.deleted),
    notFound: tally(r.notFound),
    unknown: tally(r.unknown),
    truncated: r.truncated === true,
  };
}

// The raw JSON of GET ops/approval-mail/stats made safe for the page: every
// number a number >= 0, every list a list (a day without a date and a
// recipient without an org id are dropped), the optional blocks (allowlist,
// flags, recipients, status) only when the API sent them - it leaves one
// out when its own lookup failed (partial). Order as the API sends it
// (days newest first, recipients by mails) - the page never re-sorts.
export function normalizeApprovalMailStats(raw: unknown): ApprovalMailStatsDto {
  const body = asRecord(raw);
  const allowlist = asRecord(body.allowlist);
  const flags = normalizeFlags(body.flags);
  const status = normalizeStatsStatus(body.status);
  const days = objects(body.days)
    .map((entry): ApprovalMailStatsDayDto | null => {
      const r = asRecord(entry);
      const date = (text(r.date) ?? "").trim();
      if (date === "") return null;
      return {
        date,
        mails: tally(r.mails),
        items: tally(r.items),
        cells: objects(r.cells).map(normalizeStatsCell),
      };
    })
    .filter((d): d is ApprovalMailStatsDayDto => d !== null);
  const recipients = Array.isArray(body.recipients)
    ? objects(body.recipients)
        .map((entry): ApprovalMailStatsRecipientDto | null => {
          const r = asRecord(entry);
          const organizationId = int(r.organizationId);
          if (organizationId === null) return null;
          return {
            organizationId,
            organizationName: text(r.organizationName),
            organizationType: text(r.organizationType),
            recipient: text(r.recipient) ?? "",
            mails: tally(r.mails),
            reminderMails: tally(r.reminderMails),
            skippedMails: tally(r.skippedMails),
            items: tally(r.items),
            lastAtUtc: text(r.lastAtUtc),
          };
        })
        .filter((r): r is ApprovalMailStatsRecipientDto => r !== null)
    : undefined;
  return {
    from: text(body.from),
    to: text(body.to),
    orgId: int(body.orgId),
    timeZone: text(body.timeZone),
    rows: tally(body.rows),
    truncated: body.truncated === true,
    partial: body.partial === true,
    ...(typeof allowlist.active === "boolean"
      ? {
          allowlist: {
            active: allowlist.active,
            entries: text(allowlist.entries) ?? "",
            invalid: (Array.isArray(allowlist.invalid) ? allowlist.invalid : [])
              .filter((entry): entry is string => typeof entry === "string")
              .map((entry) => entry.trim())
              .filter((entry) => entry !== ""),
          },
        }
      : {}),
    ...(flags ? { flags } : {}),
    totals: objects(body.totals).map(normalizeStatsTotal),
    days,
    ...(recipients ? { recipients } : {}),
    ...(status ? { status } : {}),
  };
}

// ----- Personal preference -----

// true/false as sent; anything else (null, absent, a string) = null =
// "follow the organisation".
const triBool = (value: unknown): boolean | null =>
  typeof value === "boolean" ? value : null;

function normalizePreferencesFlags(
  raw: unknown,
): ApprovalMailPreferencesFlagsDto | undefined {
  const r = asRecord(raw);
  const out: ApprovalMailPreferencesFlagsDto = {
    ...(typeof r.selfServiceEnabled === "boolean"
      ? { selfServiceEnabled: r.selfServiceEnabled }
      : {}),
    ...(typeof r.userPrefEnabled === "boolean"
      ? { userPrefEnabled: r.userPrefEnabled }
      : {}),
    ...(typeof r.columnsEnabled === "boolean"
      ? { columnsEnabled: r.columnsEnabled }
      : {}),
  };
  return Object.keys(out).length > 0 ? out : undefined;
}

// The raw JSON of GET/PUT profile/approval-mail-preferences made safe for
// the block: the stored choices as true/false/null (null = follow the
// organisation = today), the effective block with every field present
// (absent = the API could not say: nothing received, no colleagues), the
// organisation's address, the API's flags only when sent.
export function normalizeApprovalMailPreferences(
  raw: unknown,
): ApprovalMailPreferencesDto {
  const body = asRecord(raw);
  const eff = asRecord(body.effective);
  const effective: ApprovalMailPreferencesEffectiveDto = {
    receiveApprovalMails: bool(eff.receiveApprovalMails, false),
    receiveReminders: bool(eff.receiveReminders, false),
    isOrganizationRecipient: bool(eff.isOrganizationRecipient, false),
    optOutApplies: bool(eff.optOutApplies, false),
    otherRecipients: tally(eff.otherRecipients),
  };
  const flags = normalizePreferencesFlags(body.flags);
  return {
    userId: int(body.userId),
    organizationId: int(body.organizationId),
    email: text(body.email),
    receiveApprovalMails: triBool(body.receiveApprovalMails),
    receiveReminders: triBool(body.receiveReminders),
    effective,
    organizationRecipient: text(body.organizationRecipient),
    ...(flags ? { flags } : {}),
  };
}

// ----- Reminder run on demand -----

const counts = (raw: unknown): Record<string, number> => {
  const out: Record<string, number> = {};
  for (const [key, value] of Object.entries(asRecord(raw))) {
    const n = int(value);
    if (n !== null) out[key] = Math.max(0, n);
  }
  return out;
};

const strings = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((v) => v !== null && v !== undefined).map((v) => String(v))
    : [];

function normalizeReminderGroup(raw: unknown): ApprovalReminderGroupDto | null {
  const r = asRecord(raw);
  const organizationId = int(r.organizationId);
  if (organizationId === null) return null;
  return {
    organizationId,
    direction: (text(r.direction) ?? "").trim().toLowerCase(),
    linkOrganizationId: int(r.linkOrganizationId),
    linksUnderLinkOrg: r.linksUnderLinkOrg === true,
    counterpartOrganizationId: int(r.counterpartOrganizationId),
    mixedCounterparts: r.mixedCounterparts === true,
    reminderNumber: tally(r.reminderNumber),
    reminderMax: tally(r.reminderMax),
    members: strings(r.members),
    outcome: (text(r.outcome) ?? "").trim(),
    allowlistPreview: (text(r.allowlistPreview) ?? "").trim(),
  };
}

// The raw JSON of POST ops/approval-mail/reminders/run (the API's
// ApprovalReminderSweepResult) made safe for the page: every number a
// number >= 0, the two per-token maps records of numbers, the groups an
// array (a group without an org id is dropped), strings nullable, booleans
// only on true. closedTotal is taken from the API when sent, else summed
// from the closed map.
export function normalizeApprovalReminderRun(raw: unknown): ApprovalReminderRunDto {
  const body = asRecord(raw);
  const closed = counts(body.closed);
  const closedTotal =
    int(body.closedTotal) ?? Object.values(closed).reduce((sum, n) => sum + n, 0);
  return {
    outcome: (text(body.outcome) ?? "").trim(),
    dryRun: body.dryRun === true,
    nowUtc: text(body.nowUtc),
    runId: text(body.runId),
    candidates: tally(body.candidates),
    capped: body.capped === true,
    skippedAllowlist: tally(body.skippedAllowlist),
    allowlist: text(body.allowlist),
    skippedToday: tally(body.skippedToday),
    skippedOrgOff: tally(body.skippedOrgOff),
    notDue: tally(body.notDue),
    orgs: tally(body.orgs),
    groups: tally(body.groups),
    deferredOrgLimit: tally(body.deferredOrgLimit),
    deferredRunLimit: tally(body.deferredRunLimit),
    mailed: tally(body.mailed),
    mailedItems: tally(body.mailedItems),
    failed: tally(body.failed),
    skippedNoCounterpart: tally(body.skippedNoCounterpart),
    skipped: counts(body.skipped),
    closed,
    closedWriteFailed: body.closedWriteFailed === true,
    groupResults: objects(body.groupResults)
      .map(normalizeReminderGroup)
      .filter((g): g is ApprovalReminderGroupDto => g !== null),
    error: text(body.error),
    ms: tally(body.ms),
    closedTotal: Math.max(0, closedTotal),
    closedSummary: text(body.closedSummary),
  };
}

// A PUT answer the cache can take: a non-empty object (the settings DTO).
function isSettingsAnswer(raw: unknown): boolean {
  return (
    raw !== null &&
    typeof raw === "object" &&
    !Array.isArray(raw) &&
    Object.keys(raw as Record<string, unknown>).length > 0
  );
}

export function approvalMailSettingsKey(orgId: number): unknown[] {
  return [APPROVAL_MAIL_ROOT_KEY, "settings", orgId];
}

// The caller's own row: no org/user segment (the API reads the token).
export function approvalMailPreferencesKey(): unknown[] {
  return [APPROVAL_MAIL_ROOT_KEY, "preferences"];
}

// null (not undefined) for an absent filter part: undefined segments are
// dropped by react-query's key hashing, which would collide the unfiltered
// log with a filtered one.
export function approvalMailLogKey(
  orgId: number,
  query?: ApprovalMailLogQuery,
): unknown[] {
  return [
    APPROVAL_MAIL_ROOT_KEY,
    "log",
    orgId,
    query?.take ?? null,
    query?.itemType ?? null,
    query?.itemId ?? null,
  ];
}

// null (not undefined) for an absent part, as for the log key above.
export function approvalMailStatsKey(query: ApprovalMailStatsQuery): unknown[] {
  return [
    APPROVAL_MAIL_ROOT_KEY,
    "stats",
    query.from ?? null,
    query.to ?? null,
    query.orgId ?? null,
  ];
}

const selectLogBatches = (result: ApprovalMailLogResultDto) => result.batches;
const selectLogMeta = (result: ApprovalMailLogResultDto) => result.meta;

export function useApprovalMailRepository(): ApprovalMailRepository {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();

  function useSettings(
    orgId: number,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailSettingsDto> {
    return useQuery(
      approvalMailSettingsKey(orgId),
      async () =>
        normalizeApprovalMailSettings(
          await apiClient.getApprovalMailSettings(orgId),
        ),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: orgId > 0 && (options?.enabled ?? true),
      },
    );
  }

  // ONE cache entry per (org, filter) = the whole answer { batches, meta };
  // useLog and useLogMeta are two views (select) of it, so mounting both
  // costs ONE GET.
  const fetchLog = (orgId: number, query?: ApprovalMailLogQuery) => async () =>
    normalizeApprovalMailLogResult(await apiClient.getApprovalMailLog(orgId, query));
  const logOptions = (orgId: number, options?: { enabled?: boolean }) => ({
    staleTime: STALE_MS,
    retry: false as const,
    refetchOnMount: false as const,
    refetchOnWindowFocus: false as const,
    refetchInterval: false as const,
    // The previous filter's rows stay visible while the next filter
    // loads (no empty flash between two lists).
    keepPreviousData: true,
    enabled: orgId > 0 && (options?.enabled ?? true),
  });

  function useLog(
    orgId: number,
    query?: ApprovalMailLogQuery,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailLogBatchDto[]> {
    return useQuery(approvalMailLogKey(orgId, query), fetchLog(orgId, query), {
      ...logOptions(orgId, options),
      select: selectLogBatches,
    });
  }

  function useLogMeta(
    orgId: number,
    query?: ApprovalMailLogQuery,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailLogMetaDto> {
    return useQuery(approvalMailLogKey(orgId, query), fetchLog(orgId, query), {
      ...logOptions(orgId, options),
      select: selectLogMeta,
    });
  }

  function useStats(
    query: ApprovalMailStatsQuery,
    options?: { enabled?: boolean },
  ): Resource<ApprovalMailStatsDto> {
    return useQuery(
      approvalMailStatsKey(query),
      async () =>
        normalizeApprovalMailStats(await apiClient.getApprovalMailStats(query)),
      {
        staleTime: STATS_STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        // The previous period's tables stay visible (dimmed by the page)
        // while the next period loads.
        keepPreviousData: true,
        enabled: options?.enabled ?? true,
      },
    );
  }

  async function setSettings(
    orgId: number,
    body: PutApprovalMailSettingsRequest,
  ): Promise<void> {
    const raw = await apiClient.setApprovalMailSettings(orgId, body);
    // The fields read the settings query. The PUT answers with the full
    // settings (clamped/ignored values included): write them into the
    // cache as the new server state - 0 extra calls. Only an empty answer
    // (an older API) costs ONE refetch.
    if (isSettingsAnswer(raw)) {
      queryClient.setQueryData(
        approvalMailSettingsKey(orgId),
        normalizeApprovalMailSettings(raw),
      );
      return;
    }
    await queryClient.invalidateQueries(approvalMailSettingsKey(orgId));
  }

  function usePreferences(options?: {
    enabled?: boolean;
  }): Resource<ApprovalMailPreferencesDto> {
    return useQuery(
      approvalMailPreferencesKey(),
      async () =>
        normalizeApprovalMailPreferences(
          await apiClient.getApprovalMailPreferences(),
        ),
      {
        staleTime: STALE_MS,
        retry: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchInterval: false,
        enabled: options?.enabled ?? true,
      },
    );
  }

  async function setPreferences(
    body: PutApprovalMailPreferencesRequest,
  ): Promise<void> {
    const raw = await apiClient.putApprovalMailPreferences(body);
    // The block reads the preferences query. The PUT answers with the full
    // row (effective values recomputed): write it into the cache as the new
    // server state - 0 extra calls. Only an empty answer (an older API)
    // costs ONE refetch.
    if (isSettingsAnswer(raw)) {
      queryClient.setQueryData(
        approvalMailPreferencesKey(),
        normalizeApprovalMailPreferences(raw),
      );
      return;
    }
    await queryClient.invalidateQueries(approvalMailPreferencesKey());
  }

  async function runReminders(
    query: ApprovalReminderRunQuery,
  ): Promise<ApprovalReminderRunDto> {
    const result = normalizeApprovalReminderRun(
      await apiClient.runApprovalReminders(query),
    );
    // A real run wrote log rows: the mounted stats period re-reads itself
    // (ONE GET, refetchType "active" - nothing for unmounted periods). A
    // dry run wrote nothing, so nothing is re-read.
    if (!query.dryRun) {
      await queryClient.invalidateQueries([APPROVAL_MAIL_ROOT_KEY, "stats"]);
    }
    return result;
  }

  return {
    useSettings,
    useLog,
    useLogMeta,
    useStats,
    setSettings,
    runReminders,
    usePreferences,
    setPreferences,
  };
}
